import { supabase } from '../lib/supabase';

export interface DistributionTrack {
  id: string;
  user_id: string;
  performance_id: string;
  artist_name: string;
  original_title: string;
  original_artist: string;
  distribution_status: string;
  distributor_track_id: string | null;
  youtube_music_url: string | null;
  youtube_video_id: string | null;
  video_url: string | null;
  fuga_asset_id: string | null;
  revenue_to_date: number;
  last_payment_date: string | null;
  payment_status: string;
  rights_cleared: boolean;
  distribution_notes: string | null;
  revenue_split: {
    artist: number;
    platform: number;
  };
  created_at: string;
  updated_at: string | null;
}

export interface DistributionRights {
  id: string;
  user_id: string;
  rights_type: string;
  agreement_accepted: boolean;
  agreement_date: string | null;
  agreement_version: string | null;
  territory_restrictions: any | null;
  created_at: string;
  updated_at: string | null;
}

export class DistributionService {
  async getDistributionTracks(userId: string): Promise<DistributionTrack[]> {
    try {
      const { data, error } = await supabase
        .from('distribution_tracks')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error loading distribution tracks:', error);
      throw error;
    }
  }

  async getDistributionTrack(id: string): Promise<DistributionTrack | null> {
    try {
      const { data, error } = await supabase
        .from('distribution_tracks')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error loading distribution track:', error);
      throw error;
    }
  }

  async createDistributionTrack(trackData: Partial<DistributionTrack>): Promise<DistributionTrack> {
    try {
      const { data, error } = await supabase
        .from('distribution_tracks')
        .insert([trackData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating distribution track:', error);
      throw error;
    }
  }

  async updateDistributionTrack(id: string, trackData: Partial<DistributionTrack>): Promise<DistributionTrack> {
    try {
      const { data, error } = await supabase
        .from('distribution_tracks')
        .update(trackData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating distribution track:', error);
      throw error;
    }
  }

  async getUserDistributionRights(userId: string): Promise<DistributionRights | null> {
    try {
      const { data, error } = await supabase
        .from('distribution_rights')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error loading distribution rights:', error);
      throw error;
    }
  }

  async acceptDistributionAgreement(userId: string, rightsType: string, agreementVersion: string): Promise<DistributionRights> {
    try {
      // Check if rights record already exists
      const { data: existingRights } = await supabase
        .from('distribution_rights')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      let data;
      if (existingRights) {
        // Update existing record
        const { data: updatedData, error } = await supabase
          .from('distribution_rights')
          .update({
            rights_type: rightsType,
            agreement_accepted: true,
            agreement_date: new Date().toISOString(),
            agreement_version: agreementVersion
          })
          .eq('id', existingRights.id)
          .select()
          .single();

        if (error) throw error;
        data = updatedData;
      } else {
        // Create new record
        const { data: newData, error } = await supabase
          .from('distribution_rights')
          .insert([{
            user_id: userId,
            rights_type: rightsType,
            agreement_accepted: true,
            agreement_date: new Date().toISOString(),
            agreement_version: agreementVersion
          }])
          .select()
          .single();

        if (error) throw error;
        data = newData;
      }
      
      return data;
    } catch (error) {
      console.error('Error accepting distribution agreement:', error);
      throw error;
    }
  }

  async uploadDistributionVideo(file: File, path: string): Promise<string> {
    try {
      // Upload video to storage
      const { data, error } = await supabase.storage
        .from('distribution_videos')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('distribution_videos')
        .getPublicUrl(path);

      return publicUrl;
    } catch (error) {
      console.error(`Error uploading distribution video:`, error);
      throw error;
    }
  }

  async getDistributionRevenue(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('distribution_tracks')
        .select('revenue_to_date')
        .eq('user_id', userId);

      if (error) throw error;
      
      // Sum up all revenue from all tracks
      const totalRevenue = data.reduce((sum, track) => sum + (track.revenue_to_date || 0), 0);
      return totalRevenue;
    } catch (error) {
      console.error('Error getting distribution revenue:', error);
      throw error;
    }
  }
}